/**
 * Human-readable Title Case display labels for all canonical enum values.
 *
 * Stored enum values remain canonical (lowercase or UPPERCASE per spec);
 * only the visual representation uses Title Case with spaces.
 *
 * Import these maps in any component that displays enum values as text.
 */
import type { JobStatus, ReviewStatus, FinalDecision, GateDecision } from '@/types';

// ─── JobStatus labels ────────────────────────────────────────────────────────

export const jobStatusLabel: Record<JobStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

// ─── ReviewStatus labels ─────────────────────────────────────────────────────

export const reviewStatusLabel: Record<ReviewStatus, string> = {
  pending_review: 'Pending Review',
  claimed: 'Claimed',
  in_progress: 'In Progress',
  reviewed: 'Reviewed',
  escalated: 'Escalated',
  returned: 'Returned',
  paused_at_document_assistant: 'Paused at Document Assistant',
  paused_at_document: 'Paused at Document',
  paused_at_parties: 'Paused at Parties',
  paused_at_clause: 'Paused at Clause',
  paused_at_gate_1: 'Paused at Gate 1',
  paused_at_compliance: 'Paused at Compliance',
  paused_at_signature: 'Paused at Signature',
  paused_at_gate_2: 'Paused at Gate 2',
  paused_at_gate: 'Paused at Gate (Final Synthesis)',
  paused_at_orchestrator_bootstrap: 'Paused at Orchestrator (Final Report)',
  paused_at_orchestrator: 'Paused at Orchestrator (legacy)',
};

// ─── FinalDecision labels ────────────────────────────────────────────────────

export const finalDecisionLabel: Record<FinalDecision, string> = {
  APPROVED: 'Approved',
  APPROVED_WITH_ALERTS: 'Approved With Alerts',
  REJECTED: 'Rejected',
  ESCALATED: 'Escalated',
};

// ─── GateDecision labels ─────────────────────────────────────────────────────

export const gateDecisionLabel: Record<GateDecision, string> = {
  APPROVED: 'Approved',
  APPROVED_WITH_ALERTS: 'Approved With Alerts',
  REJECTED: 'Rejected',
  ESCALATED: 'Escalated',
  MANUAL_REVIEW_REQUIRED: 'Manual Review Required',
};

// ─── Helper functions ────────────────────────────────────────────────────────

/** Returns the display label for a JobStatus value. */
export function getJobStatusLabel(status: JobStatus): string {
  return jobStatusLabel[status];
}

/** Returns the display label for a ReviewStatus value. */
export function getReviewStatusLabel(status: ReviewStatus): string {
  return reviewStatusLabel[status];
}

/** Returns the display label for a FinalDecision value. */
export function getFinalDecisionLabel(decision: FinalDecision): string {
  return finalDecisionLabel[decision];
}

/** Returns the display label for a GateDecision value. */
export function getGateDecisionLabel(decision: GateDecision): string {
  return gateDecisionLabel[decision];
}
